// LE-00 (M-02 / promo): LINE 顧客管理・メッセージ配信の型基盤。
// 別リポ kawai-line-richmenu/functions/src/shared/types.ts の Zod schema を
// 本リポ規約に移植したもの（snake_case → camelCase / z.date() → Iso8601 /
// tenantId 必須化）。Web SDK / Node SDK / zod 非依存（fuelPriceDerive.ts と同方針）。
//
// 関連 collection（いずれもフラット path + tenantId field 必須 / SAAS-ARCHITECTURE §1.2）:
//   - customers/{autoId}          ← Customer（LINE 友だち顧客マスタ）
//   - line_events/{autoId}        ← LineEvent（webhook 等の受信イベントログ）
//   - scheduled_messages/{autoId} ← ScheduledMessage（友だち追加 N 日後配信などの予約）
//
// 設計上の注意:
//   - 本パッケージの「顧客マスタ = customers」は、promo MVP（types.ts）の
//     「顧客マスタ = users(AppUser) 拡張」とは **別 collection / 別系統**。
//     前者は webhook follow を起点に LINE 友だち全員を捕捉（予約未経験を含む）、
//     後者は予約済み顧客（Firebase Auth uid あり）の行動トラッキング。
//     興味タグ（interestTags）/ 車両（vehicles）/ WashPass 状態など共通ドメイン型は
//     types.ts から再利用し、両者で同じ promoInterest.ts 純関数を適用できるようにする。
//   - 書込は Functions（webhook / repo / scheduler）経由のみ。tenant-promo 管理画面は read のみ。
//   - LINE メッセージ本文の描画（renderer）は LINE SDK 依存のため shared には置かず、
//     M-18 で functions/src/promo/templates/registry.ts に実装する。
export const CUSTOMER_DATA_SOURCES = [
    'webhook_follow',
    'reservation_link',
    'csv_import',
    'admin_manual',
    'liff_coupon',
    'liff_survey',
    'unknown',
];
export const LINE_EVENT_TYPES = [
    'follow',
    'unfollow',
    'message_received',
    'postback_tap',
    'uri_tap',
    'survey_response',
    'message_sent',
    'coupon_displayed',
    'qr_displayed',
    'coupon_expired',
    'reservation_completed',
    'reservation_cancelled',
    'visit_confirmed',
];
export const LINE_EVENT_SOURCES = [
    'webhook',
    'reservation_system',
    'liff_coupon',
    'admin',
    'scheduler',
    'csv_import',
];
export const SCHEDULED_MESSAGE_STATUSES = [
    'pending',
    'sending',
    'sent',
    'cancelled',
    'skipped',
    'failed',
];
export const COUPON_STATUSES = [
    'draft',
    'active',
    'archived',
];
export const COUPON_KINDS = [
    'standard',
    'booking_reward',
];
export const COUPON_REDEEM_METHODS = [
    'staff',
    'qr',
];
export const COUPON_EFFECTS = [
    'none',
    'confetti',
    'lightwrap',
    'snow',
];
/** admin フォーム / customer 表示用のラベル。 */
export const COUPON_EFFECT_LABEL = {
    none: 'なし',
    confetti: '紙吹雪（落ち葉風）',
    lightwrap: '光に包まれる',
    snow: '雪が降る',
};
/**
 * クーポンの取得 / 使用エフェクトを解釈する純関数。
 * 未設定（フィールドを持たない既存クーポン）は `'confetti'`（C-61 出荷時の既定挙動）に
 * フォールバックし、見た目の互換性を保つ。
 */
export function resolveCouponEffect(value) {
    return value ?? 'confetti';
}
/** QR 使用画面（customer）の案内文の既定値（coupon.qrInstruction 未設定時のフォールバック）。 */
export const DEFAULT_COUPON_QR_INSTRUCTION = 'ご来店時にこの QR をスタッフにご提示ください。';
/** admin の案内文セレクト用プリセット（自由記述も可 / 先頭が既定値）。 */
export const COUPON_QR_INSTRUCTION_PRESETS = [
    DEFAULT_COUPON_QR_INSTRUCTION,
    '給油機で支払い方法を選択後、このQRクーポンをかざしてご利用ください。',
];
/**
 * `maxRedeemsPerUser` の「無制限」を表すセンチネル値（`0` = 無制限）。
 * `undefined`（未設定の既存クーポン）は 1 回（従来挙動）として解釈する。
 */
export const UNLIMITED_REDEEMS = 0;
/**
 * クーポンの「1 ユーザーあたり使用（redeem）可能回数」を解釈する純関数。
 * - `undefined`（旧クーポン） → 1（従来の単発使用）
 * - `UNLIMITED_REDEEMS`（0） → `Infinity`（無制限）
 * - それ以外 → その回数
 */
export function resolveMaxRedeemsPerUser(coupon) {
    const v = coupon.maxRedeemsPerUser;
    if (typeof v !== 'number')
        return 1;
    if (v === UNLIMITED_REDEEMS)
        return Infinity;
    return v;
}
export const COUPON_USAGE_STATUSES = [
    'delivered',
    'claimed',
    'redeemed',
];
// ============================================================================
// PromoPlan — 商品（業態）ごとの販促プラン（M-56 / M-90 で任意個数化 / promo_plans/{planId}）
// ============================================================================
/**
 * 販促プラン一覧の表示順（M-90 で fuel を解禁 = 集合は `PROMO_INTEREST_KEYS` と一致し、
 * **順序だけがここの責務**）。集合一致は `promoPlan.test.ts` が機械的に縛るので、
 * `PROMO_INTEREST_KEYS` に商材を足したのにプランの選択肢から落ちる、という drift は起きない。
 */
export const PROMO_PLAN_INTEREST_KEYS = [
    'inspection',
    'car_sales',
    'keeper',
    'washpass',
    'fuel',
];
/**
 * アンケート（M-78）の対象業態。燃料は設問設計が未対応なので 4 業態のまま
 * = **販促プランとは別集合**（M-90 でプラン側だけ fuel を解禁したため分離した）。
 *
 * 型注釈を付けないこと — tuple 型のまま残すことで `z.enum()` に直接渡せる
 * （`PROMO_INTEREST_KEYS` と同じ手。zod のべた書き列挙との drift を構造的に不能にする）。
 */
export const PROMO_SURVEY_INTEREST_KEYS = [
    'inspection',
    'car_sales',
    'keeper',
    'washpass',
];
/**
 * 1 テナントあたりの販促プラン上限（M-90）。UI の作成ボタン無効化と Callable ガードで共用する。
 *
 * プランは KPI 30 件 + 施策 50 件まで持てるため理論最大 50KB/件。救済エクスポート
 * （`exportTenantData` の 8MB 予算）を 1 コレクションで食い潰さないための歯止め。
 */
export const MAX_PROMO_PLANS_PER_TENANT = 50;
export const PROMO_AUTOMATION_IDS = [
    'm1_01_link_prompt',
];
/** 遅延日数の許容範囲（当日送信 = 促進の趣旨に合わないため下限 1 日）。 */
export const PROMO_AUTOMATION_MIN_DELAY_DAYS = 1;
export const PROMO_AUTOMATION_MAX_DELAY_DAYS = 365;
/** 遅延日数として保存・使用してよい値か（整数 / 範囲内 / 有限）。 */
export function isValidAutomationDelayDays(value) {
    return (typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= PROMO_AUTOMATION_MIN_DELAY_DAYS &&
        value <= PROMO_AUTOMATION_MAX_DELAY_DAYS);
}
export const PROMO_AUTOMATION_DEFINITIONS = {
    m1_01_link_prompt: {
        id: 'm1_01_link_prompt',
        name: '予約リンク促進（M1-01）',
        description: '友だち追加した人に、予約フォームへの導線を 2 回に分けて送ります。' +
            '1 通目は予約フォームの案内、2 通目は「車検期限を 60 日前に通知できる」価値訴求です。' +
            'どちらも「予約に進む / もう紐付け済み / 興味なし」の 3 択で回答でき、' +
            '回答すると残りの配信は自動で取り消されます。',
        trigger: 'follow',
        triggerLabel: '友だち追加',
        defaultSteps: [
            { templateId: 'm1_01_link_prompt_3d', days: 3 },
            { templateId: 'm1_01_link_prompt_14d', days: 14 },
        ],
        audienceConditions: [
            'まだ予約に紐付いていない人（予約経験がある人には送りません）',
            'LINE をブロック / 友だち解除していない人',
            '同じ配信が未送信で残っている人には二重に予約しません',
        ],
    },
};
/**
 * 実際に使うステップ構成を解決する（純関数）。
 *
 * **テンプレート構成（`templateId` と順序）はカタログが所有**し、doc は `days` の上書きだけを
 * 提供する。doc 側の値が不正（非整数 / 範囲外 / 欠落 / 未知 templateId）なら**その項目だけ
 * 既定に倒す** — 設定ミスで「当日いきなり全員に届く」「配信が消える」が起きないようにする。
 */
export function resolveAutomationSteps(automation, definition) {
    const overrides = Array.isArray(automation?.steps) ? automation.steps : [];
    return definition.defaultSteps.map((step) => {
        const override = overrides.find((o) => !!o && typeof o === 'object' && o.templateId === step.templateId);
        return {
            templateId: step.templateId,
            days: isValidAutomationDelayDays(override?.days) ? override.days : step.days,
        };
    });
}
/**
 * `ScheduledMessage.templateId` から、それを制御する自動配信の id を引く（純関数）。
 *
 * poller が送信直前に ON/OFF を確認するために使う（M-89 PR-B）。カタログに無い
 * templateId は `null` — 自動配信の管理外なので gate の対象にしない。
 */
export function resolveAutomationIdByTemplateId(templateId) {
    for (const id of PROMO_AUTOMATION_IDS) {
        const def = PROMO_AUTOMATION_DEFINITIONS[id];
        if (def.defaultSteps.some((s) => s.templateId === templateId))
            return id;
    }
    return null;
}
/** `promo_automations` の doc id（決定論的 / 1 テナント 1 自動配信）。 */
export function promoAutomationDocId(tenantId, automationId) {
    return `${tenantId}__${automationId}`;
}
/**
 * その自動配信が有効か（純関数）。
 *
 * **doc 不在（`null` / `undefined`）は OFF**。`enabled === true` の明示だけを有効とみなし、
 * 欠損・型不正はすべて停止側に倒す（「設定し忘れたら勝手に配信される」を構造的に防ぐ）。
 */
export function isPromoAutomationEnabled(automation) {
    return automation?.enabled === true;
}
// ============================================================================
// PromoSurvey — アンケート定義・回答（M-78 / promo_surveys, promo_survey_responses）
// ============================================================================
/** 選択肢の既定表示文言（テナントは `PromoSurvey.choiceLabels` で個別上書き可）。 */
export const DEFAULT_SURVEY_CHOICE_LABELS = {
    not_interested: '今は関心がない',
    slightly_interested: '少し気になっている',
    interested_undecided: '関心はあるが時期は未定',
    interested_planned: '関心があり近いうちに検討したい',
};
/** 車検満了日設問の既定表示文言（テナントは `PromoSurvey.inspectionDueLabel` で上書き可）。 */
export const DEFAULT_INSPECTION_DUE_LABEL = 'お車の車検満了日を教えてください';
/**
 * 選択肢 id の最大長（M-106 / **保存側 zod と postback 検証の唯一の根拠**）。
 *
 * `functions/src/promo/survey.ts` の `z.string().trim().min(1).max(...)` は
 * **この定数を import する**こと。数字を両側に書くと、片方だけ動かしたときに
 * 「管理画面で保存できるのに、トーク版の postback だけ黙って落ちる」= 回答が
 * 記録されない無言の穴になる（受信側は形式で弾いた postback を `postback_tap` へ
 * 落とすため、失敗ログすら出ない）。
 */
export const SURVEY_OPTION_ID_MAX = 128;
/**
 * 選択肢 id として受理する形式か（純関数 / M-106）。
 *
 * **保存側 zod（`z.string().trim().min(1).max(SURVEY_OPTION_ID_MAX)`）と同値**である
 * ことが唯一の要件。zod の `.trim()` は変換なので、
 *
 *   - 判定は**トリム後の長さ**で行う（`' '.repeat(1) + 'a'.repeat(128)` は通る）
 *   - Firestore に入る id は常にトリム済み → 呼出側もトリム後の値を使うこと
 *
 * **文字種は制限しない**（`isSafeSurveyDocId` と同じ判断）。zod が許す形をここで
 * 追加で弾くと上記の無言の穴を自分で作ることになる。`\n` / `\t` のような空白扱いの
 * 制御文字はトリムで消えて長さ 0 になるため**両側で同じく落ちる**（= 一致しており、
 * ここだけで弾いているのではない）。
 */
export function isValidSurveyOptionId(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    return trimmed.length >= 1 && trimmed.length <= SURVEY_OPTION_ID_MAX;
}
/** 待機日数の許容範囲（0 日は「待機を置く意味が無い」ため下限 1 日 / 自動配信と同じ上限）。 */
export const PROMO_SCENARIO_MIN_WAIT_DAYS = 1;
export const PROMO_SCENARIO_MAX_WAIT_DAYS = 365;
/** 1 シナリオのノード数上限（poller の 1 実行あたりの走査量を有限に保つ）。 */
export const PROMO_SCENARIO_MAX_NODES = 50;
/** 1 分岐あたりの枝数上限（UI の縦積み表示が破綻しない範囲）。 */
export const PROMO_SCENARIO_MAX_BRANCH_ARMS = 10;
/** 1 人あたりの送信上限の許容範囲と既定値（打ち止めの安全弁）。 */
export const PROMO_SCENARIO_MIN_SENDS_PER_RUN = 1;
export const PROMO_SCENARIO_MAX_SENDS_PER_RUN = 20;
export const PROMO_SCENARIO_DEFAULT_SENDS_PER_RUN = 5;
/**
 * poller の 1 実行で連続処理してよいステップ数の上限。
 *
 * branch / goal は待機を挟まず同一実行内で連続処理するため、万一グラフ検証をすり抜けた
 * 閉路があっても**ここで必ず止まる**（検証と合わせた二重の歯止め）。
 */
export const PROMO_SCENARIO_MAX_STEPS_PER_RUN = 20;
//# sourceMappingURL=promoTypes.js.map